import React, { useEffect, useCallback, useState } from 'react';
import './PokemonCardDetail.css';

function PokemonCardDetail({ card, onClose, apiUrl, onPokedexClick, onArtistClick, onSetClick }) {
  const [imageError, setImageError] = useState(false);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const lang = card.lang || 'en';
  const imageUrl = `${apiUrl}/images/pokemon/${lang}/${card.id}.png`;

  const getTypeClass = (type) => {
    return type ? `type-${type.toLowerCase()}` : '';
  };

  // Render energy cost as colored badges
  const renderEnergyCost = (cost) => {
    if (!cost || cost.length === 0) return null;
    return (
      <div className="pokemon-energy-cost">
        {cost.map((energy, i) => (
          <span key={i} className={`pokemon-energy-badge ${getTypeClass(energy)}`}>
            {energy.charAt(0)}
          </span>
        ))}
      </div>
    );
  };

  const handlePokedexClick = (dexNumber) => {
    if (onPokedexClick) {
      onPokedexClick(dexNumber);
    }
  };

  return (
    <div className="pokemon-detail-overlay" onClick={handleBackdropClick}>
      <div className="pokemon-detail-modal">
        <button className="pokemon-close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>
        
        <div className="pokemon-detail-content">
          {/* Card Image */}
          <div className="pokemon-detail-image-column">
            <div className="pokemon-detail-image">
              {!imageError ? (
                <img
                  src={imageUrl}
                  alt={card.name}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="pokemon-image-placeholder">
                  <span className="pokemon-placeholder-symbol">⚡</span>
                  <span className="pokemon-placeholder-text">Image not available</span>
                </div>
              )}
            </div>
          </div>

          {/* Card Info */}
          <div className="pokemon-detail-info">
            <header className="pokemon-card-header">
              <h2 className="pokemon-card-title">{card.name}</h2>
            </header>

            {/* Type and HP badges - below header */}
            {(card.types?.length > 0 || card.hp) && (
              <div className="pokemon-type-hp-row">
                {card.types && card.types.length > 0 && (
                  <div className="pokemon-card-types">
                    {card.types.map(type => (
                      <span key={type} className={`pokemon-type-badge ${getTypeClass(type)}`}>
                        {type}
                      </span>
                    ))}
                  </div>
                )}
                {card.hp && (
                  <span className="pokemon-card-hp-badge">{card.hp} HP</span>
                )}
              </div>
            )}

            {/* Supertype & Subtypes */}
            <div className="pokemon-card-category">
              <span className="pokemon-supertype">{card.supertype}</span>
              {card.subtypes && card.subtypes.length > 0 && (
                <span className="pokemon-subtypes">
                  — {card.subtypes.join(', ')}
                </span>
              )}
              {card.level && (
                <span className="pokemon-level">Lv. {card.level}</span>
              )}
            </div>

            {/* Evolution */}
            {card.evolvesFrom && (
              <div className="pokemon-evolution">
                Evolves from <strong>{card.evolvesFrom}</strong>
              </div>
            )}

            {/* Abilities */}
            {card.abilities && card.abilities.length > 0 && (
              <div className="pokemon-abilities">
                <h3 className="pokemon-section-title">Abilities</h3>
                {card.abilities.map((ability, i) => (
                  <div key={i} className="pokemon-ability">
                    <div className="pokemon-ability-header">
                      <span className="pokemon-ability-type">{ability.type}</span>
                      <span className="pokemon-ability-name">{ability.name}</span>
                    </div>
                    <p className="pokemon-ability-text">{ability.text}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Attacks */}
            {card.attacks && card.attacks.length > 0 && (
              <div className="pokemon-attacks">
                <h3 className="pokemon-section-title">Attacks</h3>
                {card.attacks.map((attack, i) => (
                  <div key={i} className="pokemon-attack">
                    <div className="pokemon-attack-header">
                      {renderEnergyCost(attack.cost)}
                      <span className="pokemon-attack-name">{attack.name}</span>
                      {attack.damage && (
                        <span className="pokemon-attack-damage">{attack.damage}</span>
                      )}
                    </div>
                    {attack.text && (
                      <p className="pokemon-attack-text">{attack.text}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Rules */}
            {card.rules && card.rules.length > 0 && (
              <div className="pokemon-rules">
                {card.rules.map((rule, i) => (
                  <p key={i} className="pokemon-rule-text">{rule}</p>
                ))}
              </div>
            )}

            {/* Weakness/Resistance/Retreat */}
            <div className="pokemon-combat-stats">
              {card.weaknesses && card.weaknesses.length > 0 && (
                <div className="pokemon-stat-item">
                  <span className="pokemon-stat-label">Weakness</span>
                  <div className="pokemon-stat-values">
                    {card.weaknesses.map((w, i) => (
                      <span key={i} className={`pokemon-stat-badge ${getTypeClass(w.type)}`}>
                        {w.type} {w.value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {card.resistances && card.resistances.length > 0 && (
                <div className="pokemon-stat-item">
                  <span className="pokemon-stat-label">Resistance</span>
                  <div className="pokemon-stat-values">
                    {card.resistances.map((r, i) => (
                      <span key={i} className={`pokemon-stat-badge ${getTypeClass(r.type)}`}>
                        {r.type} {r.value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {card.retreatCost && card.retreatCost.length > 0 && (
                <div className="pokemon-stat-item">
                  <span className="pokemon-stat-label">Retreat Cost</span>
                  <div className="pokemon-stat-values">
                    {renderEnergyCost(card.retreatCost)}
                  </div>
                </div>
              )}
            </div>

            {/* Card Details Grid */}
            <div className="pokemon-details-grid">
              <div className="pokemon-detail-item">
                <span className="pokemon-detail-label">Set</span>
                <span className="pokemon-detail-value">
                  <button
                    className="pokemon-clickable-link"
                    onClick={() => onSetClick && onSetClick(card.set)}
                    title={`Search for cards in ${card.set}`}
                  >
                    {card.set}
                  </button>
                  {' '}({card.setCode})
                </span>
              </div>

              <div className="pokemon-detail-item">
                <span className="pokemon-detail-label">Number</span>
                <span className="pokemon-detail-value">{card.number}</span>
              </div>

              {card.rarity && (
                <div className="pokemon-detail-item">
                  <span className="pokemon-detail-label">Rarity</span>
                  <span className="pokemon-detail-value">{card.rarity}</span>
                </div>
              )}

              {card.artist && (
                <div className="pokemon-detail-item">
                  <span className="pokemon-detail-label">Artist</span>
                  <span className="pokemon-detail-value">
                    <button
                      className="pokemon-clickable-link"
                      onClick={() => onArtistClick && onArtistClick(card.artist)}
                      title={`Search for cards by ${card.artist}`}
                    >
                      {card.artist}
                    </button>
                  </span>
                </div>
              )}

              {card.regulationMark && (
                <div className="pokemon-detail-item">
                  <span className="pokemon-detail-label">Regulation</span>
                  <span className="pokemon-detail-value pokemon-regulation-mark">
                    {card.regulationMark}
                  </span>
                </div>
              )}

              {card.nationalPokedexNumbers && card.nationalPokedexNumbers.length > 0 && (
                <div className="pokemon-detail-item">
                  <span className="pokemon-detail-label">Pokédex #</span>
                  <span className="pokemon-detail-value">
                    {card.nationalPokedexNumbers.map((num, i) => (
                      <button
                        key={num}
                        className="pokemon-pokedex-link"
                        onClick={() => handlePokedexClick(num)}
                      >
                        #{num}
                      </button>
                    ))}
                  </span>
                </div>
              )}
            </div>

            {/* Flavor Text */}
            {card.flavorText && (
              <div className="pokemon-flavor-text">
                <em>{card.flavorText}</em>
              </div>
            )}

            {/* Legalities - Show all formats, default to not_legal if not specified */}
            <div className="pokemon-legalities">
              <h3 className="pokemon-section-title">Format Legality</h3>
              <div className="pokemon-legalities-grid">
                {['standard', 'expanded', 'unlimited'].map(format => {
                  const status = card.legalities?.[format] || 'Not Legal';
                  const statusClass = status.toLowerCase().replace(' ', '-');
                  return (
                    <div 
                      key={format} 
                      className={`pokemon-legality-item legality-${statusClass}`}
                    >
                      <span className="pokemon-legality-format">
                        {format.charAt(0).toUpperCase() + format.slice(1)}
                      </span>
                      <span className="pokemon-legality-status">{status}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TCGPlayer Link */}
            {card.tcgplayer_link && (
              <div className="pokemon-tcgplayer-section">
                <a 
                  href={card.tcgplayer_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="pokemon-tcgplayer-link"
                >
                  View on TCGPlayer
                  <span className="pokemon-external-icon">↗</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PokemonCardDetail;
